import { fmtBase, fmtBaseOrDash, fmtBasePrecise, fmtCountUnit } from '../../../utils/reports/shared';
import { VarianceRow, VariancePoolMember } from '../../../utils/reports/inventoryVariance';
import { ConsumptionCustomRow } from '../../../utils/reports/consumptionReport';

/**
 * Display rules for reconciled leakage rows, and specifically for the ALTERNATIVE POOL rows
 * that stand for several interchangeable products at once. Extracted from `LeakageTab` because
 * every rule here is a way the table could quietly lie, and a lie in a stock report is worth a
 * test: a placeholder rendered as a real quantity, or a synthetic pool key used where a product
 * key belongs, both look completely normal on screen.
 *
 * Pure and React-free so it can be tested directly.
 */

/**
 * Whether a row's quantities mean anything.
 *
 * Two independent ways a row can have no quantity in any space, and both must be caught here
 * so no renderer has to remember either:
 *  - a POOLED row whose members mix std units (a 'p' primary with a 'g' alternative) — only its
 *    € figures reconcile, and `pool.quantitiesComparable` is the engine's declaration of that;
 *  - ANY row flagged `countQtyUnreadable`, whose opening and closing counts were frozen in unit
 *    spaces nothing could bridge (a 3 L bottle counted once as 1 piece and once as 3000 ml).
 *    Its section quantities are placeholder zeroes for exactly the same reason a mixed pool's
 *    are, and only its frozen € are facts.
 *
 * The FLAGS are the guard to trust rather than the payload: they state the CONDITION, whereas
 * the payload for such a row has already been both a literal 0 and a null within one day of
 * development. Rendering a placeholder 0 as real stock would invent inventory on precisely the
 * rows a reader cannot sanity-check by eye.
 */
export function hasComparableQty(row: VarianceRow): boolean {
  if (row.countQtyUnreadable) return false;
  return row.pool === undefined || row.pool.quantitiesComparable;
}

/** Extra depletion relative to theoretical usage. Positive means unexplained loss;
 * negative means less stock was used than the recipes predict. Kept separate from
 * variancePct, whose expected-value denominator still drives risk classification.
 */
export function leakageUsagePercentage(row: VarianceRow): number | null {
  if (!hasComparableQty(row) || row.varianceQty === null || row.neededQty === null || row.neededQty <= 0) return null;
  const percentage = -row.varianceQty / row.neededQty * 100;
  return Number.isFinite(percentage) ? percentage : null;
}

/**
 * Quantity cell text for a row: a dash when the row's quantities aren't comparable, and also
 * whenever the value itself is absent. Both signals are honoured on purpose — either alone is
 * enough to mean "no quantity here", so the cell stays correct no matter which one the engine
 * sends.
 */
export function rowQtyText(row: VarianceRow, qty: number | null): string {
  return qty !== null && hasComparableQty(row) ? fmtBase(qty, row.stdUnit) : '—';
}

/**
 * Same guard, precise formatting — for per-portion figures (a recipe's "120 g per dish").
 * Needed because a mixed-unit pool still renders breakdown cards for setups that sold
 * nothing, and those would otherwise print a bare number against an empty unit.
 */
export function rowQtyPreciseText(row: VarianceRow, qty: number | null): string {
  return qty !== null && hasComparableQty(row) ? fmtBasePrecise(qty, row.stdUnit) : '—';
}

/**
 * A pool member's own quantity. `null` means the member was never counted — its stock is
 * unknown, which is NOT the same as being counted at zero, and rendering "0" would invent an
 * observation nobody made.
 */
export function memberQtyText(qty: number | null, unit: string): string {
  return qty === null ? '—' : fmtBase(qty, unit);
}

/**
 * A custom ingredient's quantity. Its two counts can be frozen in unbridgeable spaces too,
 * and the engine then sends null rather than a 0 — rendering that as "0.00 kg" is phantom
 * stock, which is the whole reason `fmtBaseOrDash` exists.
 */
export function customQtyText(row: ConsumptionCustomRow, qty: number | null): string {
  return fmtBaseOrDash(qty, row.stdUnit);
}

// --- unit mode ------------------------------------------------------------

/** Standardised base units (kg / L / pcs) or the unit staff actually count in. */
export type LeakageUnitMode = 'std' | 'count';

/**
 * Every quantity a leakage screen prints goes through ONE of these, so the guards above
 * cannot be bypassed and so ~25 call sites need no `mode` argument of their own. A
 * placeholder rendered as a real quantity looks completely normal on screen; a single
 * object is what keeps `hasComparableQty` unbypassable.
 */
export interface LeakageQtyFormat {
  /** Which mode built this formatter — for labelling an export, never for branching a cell. */
  mode: LeakageUnitMode;
  row(row: VarianceRow, qty: number | null): string;
  rowPrecise(row: VarianceRow, qty: number | null): string;
  member(member: VariancePoolMember, qty: number | null): string;
  custom(row: ConsumptionCustomRow, qty: number | null): string;
}

/**
 * Today's behaviour, byte for byte — the default, the fallback for every unresolved row, and
 * what the drawer's "Needed quantity breakdown" stays on whatever the toggle says (that block
 * is recipe territory and reads in the unit the recipe is written in).
 */
export const LEAKAGE_QTY_STD: LeakageQtyFormat = {
  mode: 'std',
  row: rowQtyText,
  rowPrecise: rowQtyPreciseText,
  member: (member, qty) => memberQtyText(qty, member.stdUnit),
  custom: customQtyText,
};

/** A resolved display basis, i.e. this thing's quantities can be re-expressed. */
function basisOf(x: {
  displayCountUnit?: string | null;
  displayStdPerCountUnit?: number | null;
}): { unit: string; factor: number } | null {
  return x.displayCountUnit && x.displayStdPerCountUnit && x.displayStdPerCountUnit > 0
    ? { unit: x.displayCountUnit, factor: x.displayStdPerCountUnit }
    : null;
}

/**
 * The formatter for one unit mode. `'std'` is the identity — exactly today's output — which
 * is what makes the count-unit view an opt-in that cannot move a number nobody asked to move.
 *
 * In `'count'` mode a quantity converts ONLY when the engine resolved a basis for that exact
 * row/member (`displayStdPerCountUnit`); everything else falls straight through to the std
 * formatter, dashes included. A row with no resolvable unit prints std units rather than a
 * dash — a dash would hide real leakage, and the unit prints inline so the cell stays
 * self-describing either way.
 */
export function makeLeakageQtyFormat(mode: LeakageUnitMode): LeakageQtyFormat {
  if (mode === 'std') return LEAKAGE_QTY_STD;
  return {
    mode,
    row: (row, qty) => {
      const basis = qty !== null && hasComparableQty(row) ? basisOf(row) : null;
      return basis ? fmtCountUnit((qty as number) / basis.factor, basis.unit) : rowQtyText(row, qty);
    },
    rowPrecise: (row, qty) => {
      const basis = qty !== null && hasComparableQty(row) ? basisOf(row) : null;
      return basis ? fmtCountUnit((qty as number) / basis.factor, basis.unit) : rowQtyPreciseText(row, qty);
    },
    member: (member, qty) => {
      const basis = qty !== null ? basisOf(member) : null;
      return basis ? fmtCountUnit((qty as number) / basis.factor, basis.unit) : memberQtyText(qty, member.stdUnit);
    },
    custom: (row, qty) => {
      const basis = qty !== null ? basisOf(row) : null;
      return basis ? fmtCountUnit((qty as number) / basis.factor, basis.unit) : customQtyText(row, qty);
    },
  };
}

/**
 * The real product key behind a row. A pooled row's own `key` is synthetic (`pool:<id>`) and
 * matches no product, so anything naming a product — the cost-price link flow, supplier and
 * category lookups, the favourite it writes — must go through the pool's PRIMARY member.
 * Passing `row.key` there links against a product that does not exist.
 */
export function anchorKeyOf(row: VarianceRow): string {
  return row.members?.find((m) => m.isPrimary)?.key ?? row.key;
}

/** Every product key a row can be starred through — a pool's members, or the row itself. */
export function favoriteKeysOf(row: VarianceRow): string[] {
  return row.members?.map((m) => m.key) ?? [row.key];
}

/** A pool reads as starred as soon as ANY member is (matches the utilization report). */
export function isRowFavorite(row: VarianceRow, favoriteKeys: string[]): boolean {
  return favoriteKeysOf(row).some((k) => favoriteKeys.includes(k));
}

/**
 * Next favourites list after toggling a row. Starring writes the PRIMARY member's key;
 * unstarring clears every member's, so a pool starred through an alternative can still be
 * switched off in one click instead of appearing stuck on.
 */
export function toggleFavoriteKeys(row: VarianceRow, favoriteKeys: string[]): string[] {
  const memberKeys = favoriteKeysOf(row);
  return isRowFavorite(row, favoriteKeys)
    ? favoriteKeys.filter((k) => !memberKeys.includes(k))
    : [...favoriteKeys, anchorKeyOf(row)];
}

/**
 * Search predicate. A pooled row is named after its anchor only, and its members' own rows are
 * gone from the table — so searching an alternative has to find the pool that absorbed it, or
 * the product looks like it vanished from the report.
 */
export function rowMatchesSearch(row: VarianceRow, query: string): boolean {
  if (row.productName.toLowerCase().includes(query)) return true;
  return (row.members ?? []).some((m) => m.productName.toLowerCase().includes(query));
}
